/**
 * ProvenanceModal — "where did this number come from?", in two shapes.
 *
 *   Shape A (kind: 'direct')  the source table, scrolled to the cited cell, with
 *                             that cell's values in a strip above the grid.
 *   Shape B (kind: 'derived') the audit trail: a plain sentence and worked
 *                             calculation (from `op`), then every reference; a
 *                             reference opens in a dialog stacked above it.
 *
 * A NUMBER A SCRIPT COMPUTED STILL HAS TO OPEN. A figure lifted from a table
 * points at one cell and this modal walks the reader to it. A Shapley value
 * over a 216-row panel points at no single cell — it depends on every row — so
 * it cites whole datasets (`dataset` refs), the code that ran (`script`), and
 * carries a run receipt saying whether re-running that code reproduced it. All
 * three open here; a filename in a box is a dead end dressed as a trail.
 * See design/2026-09-12-script-provenance.md.
 *
 * DATA IS INJECTED, NEVER FETCHED. `loadDataset` — and, for the script viewer,
 * `loadScript` — are supplied by the caller, so the same component works in
 * agent chat (an HTTP read) and inside a published static page (a local fetch
 * of a sibling file). This is the opposite of TableCitationModal, which renders
 * PlatinumTableWrapper and lets it fetch — that wrapper consumes four React
 * contexts plus React Query, router5 and localStorage, none of which exist in a
 * published page, which is exactly why the UMD bundle excludes it.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, Stack, Typography, alpha,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import { PlatinumTable } from '../platinum-table-new/PlatinumTable'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import CompositeTable from './CompositeTable'
import type {
  CellProvenance, CellRef, DatasetRef, ProvenanceRef, ProvenanceSidecar, RunReceipt, ScriptRef, Unit,
} from './provenance.generated'
import { explainOp, inUnit, type OpExplanation } from './explainOp'

export type DatasetLoader = (name: string) => Promise<PlatinumData>

/**
 * Reads a file the app ships, by the path a `script` ref names (always inside
 * the app directory — `compose_table` copies an outside script in and rewrites
 * the ref). Optional, and deliberately NOT defaulted to a relative `fetch`:
 * this component renders both inside the Platinum SPA and inside a published
 * static page, and a relative fetch that lands on the SPA's index.html would
 * show a page of HTML confidently labelled as the code that produced the
 * number. With no loader the script ref stays a plain record, as before.
 */
export type TextLoader = (appRelativePath: string) => Promise<string>

/**
 * The app directory a published page IS, relative to the chat's folder. A
 * webapp lives in the folder's `data/` — datasets and shipped scripts are
 * SIBLINGS of its index.html — so `data/engine.py` in a ref and `engine.py` in
 * the published page are the same file. See webviz/data-loader.js (DATA_DIR).
 */
const APP_DIR = 'data/'

/**
 * Where every chat's folder was also mounted until 12 Oct 2026. Nothing is
 * mounted there now and nothing new is written with it, but a composite stored
 * before then names its scripts that way and is still read. THIS IS THE ONE
 * PLACE THE FRONTEND'S PROVENANCE CODE KNOWS THAT NAME
 * (scripts/check-no-workspace-literal.sh holds the line).
 */
const LEGACY_FOLDER_ROOT = '/workspace/'

/**
 * A script ref's path as the app itself addresses it, or null when the app does
 * not contain the file.
 *
 * Mirrors compose_table's `shipsInApp` (agent-runner/tools/plugins/compose_table.ts):
 * a ref path is relative to the chat's folder (compose_table stores it that
 * way, `portableRef`), and only the folder's `data/` rides inside the
 * registered artifact. compose_table copies an outside script in and rewrites
 * the ref — but composites already in the wild still carry a script outside
 * `data/` (`compose_outputs.py`, by the old absolute name), a file the
 * published page does not have. Such a ref must stay a plain record: a "View
 * code" button that opens an error is a worse dead end than a filename that
 * never claimed to open. Any other absolute path is outside the folder, and
 * is not in the app either.
 *
 * The return value is relative to the APP directory, not to the folder — the
 * same convention `loadDataset` uses for dataset names, so each host applies
 * its own layout (a sibling fetch in a published page, an artifact lookup in
 * chat) exactly once.
 */
export function appRelativeScriptPath(scriptPath: string): string | null {
  const raw = scriptPath.trim()
  if (!raw) return null
  // In the folder's own terms: an old absolute name loses its root, any other
  // absolute path is outside the folder.
  let inFolder = raw
  if (raw.startsWith('/')) {
    if (!raw.startsWith(LEGACY_FOLDER_ROOT)) return null
    inFolder = raw.slice(LEGACY_FOLDER_ROOT.length)
  }
  const segments: string[] = []
  for (const segment of inFolder.split('/')) {
    if (!segment || segment === '.') continue
    // Above the folder is outside the app, whatever comes after.
    if (segment === '..') { if (segments.length === 0) return null; segments.pop(); continue }
    segments.push(segment)
  }
  const resolved = segments.join('/')
  return resolved.startsWith(APP_DIR) && resolved.length > APP_DIR.length
    ? resolved.slice(APP_DIR.length)
    : null
}

export interface ProvenanceModalProps {
  open: boolean
  onClose: () => void
  provenance: CellProvenance | null
  loadDataset: DatasetLoader
  /** Supplied by hosts that can read the app's own files; see TextLoader. */
  loadScript?: TextLoader
  /**
   * The composite's run receipt — `provenance.run` on the sidecar, one per
   * composite rather than one per cell. Optional: most composites have none,
   * and a host that does not thread it through simply shows no receipt.
   */
  run?: RunReceipt
  title?: string
  /**
   * Buttons under the dialog, offered by the host page (a presentation's "Ask
   * about this figure"). Each closes the dialog after it runs, because what
   * it starts happens outside it. None: no action row.
   */
  actions?: ProvenanceModalAction[]
}

export interface ProvenanceModalAction {
  label: string
  onClick: () => void
}

const refKey = (ref: ProvenanceRef, i: number) =>
  ref.type === 'cell' ? `cell:${ref.dataset}:${ref.row}:${ref.col}:${i}`
    : ref.type === 'dataset' ? `dataset:${ref.dataset}:${i}`
      : `${ref.type}:${i}`

const METRIC_NAME: Record<CellRef['metric'], string> = { freq: 'count', colpc: 'column %', rowpc: 'row %' }

/** The metrics a crosstab cell can hold, in the order the source-table header shows them. */
const CITED_METRICS: Array<[CellRef['metric'], string]> = [['freq', 'Count'], ['colpc', 'Column %'], ['rowpc', 'Row %']]

/** How a composite's own cell should be labelled — by its provenance `unit`,
 *  never by the raw metric name. A composite's `freq` is unscaled and reused
 *  for any unit, so "Count" is simply wrong once the source is a composite. */
const UNIT_NAME: Record<Unit, string> = { fraction: 'Percent', percent: 'Percent', count: 'Count', raw: 'Value', pp: 'Percentage points' }

/** What each kind of reference is, said in the reader's words. The two dataset
 *  roles get their own heading because "the inputs are named" and "the middle
 *  is shown" are different promises, and a reader checking the working needs to
 *  see which of them was kept. */
const GROUP_TITLE = {
  cell: 'Source cells',
  input: 'Data the script read',
  intermediate: 'Built along the way',
  code: 'Code',
} as const

/** A source dataset that carries a provenance sidecar is itself a composite.
 *  Reading `.provenance` off the loaded PlatinumData works because
 *  compose_table writes it as an extra top-level key on the same JSON file
 *  (`{ ...platinumData, provenance: sidecar }`), not a separate file. */
const sidecarOf = (data: PlatinumData): ProvenanceSidecar | undefined =>
  (data as PlatinumData & { provenance?: ProvenanceSidecar }).provenance

const provenanceFor = (data: PlatinumData, row: number, col: number): CellProvenance | undefined =>
  sidecarOf(data)?.cells?.[`${row},${col}`]

/** A source cell's stored value, for reading. colpc/rowpc are stored as
 *  fractions; the exact stored figure lives in the technical detail. */
const readableValue = (ref: CellRef) =>
  !Number.isFinite(ref.sourceValue) ? '—'
    : ref.metric === 'freq' ? ref.sourceValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : `${(ref.sourceValue * 100).toFixed(2)}%`

/** A dataset's shape, when it was recorded. Said in words rather than "216×6",
 *  because a bare pair of numbers does not say which is which. */
const datasetShape = (ref: DatasetRef): string | null =>
  typeof ref.rows === 'number' && typeof ref.cols === 'number'
    ? `${ref.rows.toLocaleString()} rows × ${ref.cols.toLocaleString()} columns`
    : null

/** The exact record of a reference: data indices and the stored value, unrounded. */
const refTechnical = (ref: ProvenanceRef, i: number): string[] => {
  switch (ref.type) {
    case 'cell':
      return [`refs[${i}]: ${ref.dataset} row ${ref.row}, column ${ref.col} (${ref.rowLabel} × ${ref.colLabel}) · ${ref.metric} stored ${ref.sourceValue}`]
    case 'dataset': {
      const shape = typeof ref.rows === 'number' && typeof ref.cols === 'number' ? ` ${ref.rows}x${ref.cols}` : ''
      return [`refs[${i}]: dataset ${ref.dataset} (${ref.role})${shape}${ref.note ? ` · ${ref.note}` : ''}`]
    }
    case 'script':
      return [`refs[${i}]: script ${ref.path}${ref.command ? ` · ${ref.command}` : ''}`]
    case 'command':
      return [`refs[${i}]: command ${ref.command}`]
  }
}

/** The receipt's exact record: hashes and a timestamp, which are for checking,
 *  not for reading, and so live behind the same disclosure as everything else. */
const runTechnical = (run: RunReceipt): string[] => [
  `run: ${run.command}`,
  ...(run.script ? [`run script: ${run.script}`] : []),
  ...(run.scriptSha256 ? [`run scriptSha256: ${run.scriptSha256}`] : []),
  ...(run.inputs ?? []).map((i) => `run input: ${i.dataset}${i.sha256 ? ` sha256=${i.sha256}` : ''}`),
  ...(run.ranAt ? [`ran at: ${run.ranAt}`] : []),
  ...(typeof run.reproduced === 'boolean' ? [`reproduced: ${run.reproduced}`] : []),
]

/** The shared shell of every reference that opens something: a card that is
 *  plainly a button — bordered, a hover and a focus ring, and a named
 *  affordance on the right — because a list row that looks like text is never
 *  clicked. One shell, so a cell, a dataset and a script all behave alike. */
const OpenableRefCard: React.FC<{
  label: string
  onOpen: () => void
  primary: React.ReactNode
  secondary: React.ReactNode
  action: string
  icon: React.ReactNode
}> = ({ label, onOpen, primary, secondary, action, icon }) => (
  <ButtonBase
    onClick={onOpen}
    aria-label={label}
    sx={(theme) => ({
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      px: 2,
      py: 1.25,
      textAlign: 'left',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1.5,
      transition: theme.transitions.create(['border-color', 'background-color']),
      '&:hover': { borderColor: 'info.main', bgcolor: alpha(theme.palette.info.main, 0.06) },
      '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 2 },
    })}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>{primary}{secondary}</Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'info.main', flexShrink: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 500 }}>{action}</Typography>
      {icon}
    </Box>
  </ButtonBase>
)

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace'

/** A cell reference: opens the source table, focused on that cell. */
const CellRefCard: React.FC<{ cellRef: CellRef; onOpen: () => void }> = ({ cellRef, onOpen }) => (
  <OpenableRefCard
    label={`Open ${cellRef.rowLabel} × ${cellRef.colLabel} in the source table ${cellRef.dataset}`}
    onOpen={onOpen}
    action="Open in table"
    icon={<TableChartOutlinedIcon fontSize="small" />}
    primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{cellRef.rowLabel} × {cellRef.colLabel}</Typography>}
    secondary={(
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {cellRef.dataset} · {METRIC_NAME[cellRef.metric]} · {readableValue(cellRef)}
      </Typography>
    )}
  />
)

/** A dataset reference: opens the WHOLE table, because there is no cited cell
 *  in it — that is the point of the ref type. An 'intermediate' opens as a
 *  composite, so its own cells keep their provenance and the trail goes on. */
const DatasetRefCard: React.FC<{ datasetRef: DatasetRef; onOpen: () => void }> = ({ datasetRef, onOpen }) => {
  const detail = [datasetShape(datasetRef), datasetRef.note].filter(Boolean).join(' · ')
  return (
    <OpenableRefCard
      label={`Open the dataset ${datasetRef.dataset} as a table`}
      onOpen={onOpen}
      action="Open table"
      icon={<TableChartOutlinedIcon fontSize="small" />}
      primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{datasetRef.dataset}</Typography>}
      secondary={detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{detail}</Typography>
      ) : null}
    />
  )
}

/** A script reference. Openable when the host can read the app's files; a
 *  plain record when it cannot. Never a DISABLED button: greyed-out text reads
 *  as "unavailable", when it is simply a record with nowhere to go. */
const ScriptRefCard: React.FC<{ scriptRef: ScriptRef; onOpen?: () => void }> = ({ scriptRef, onOpen }) => {
  const secondary = scriptRef.command ? `ran as: ${scriptRef.command}` : 'script in the app directory'
  if (!onOpen) return <RecordRefCard primary={scriptRef.path} secondary={secondary} />
  return (
    <OpenableRefCard
      label={`Open the code of ${scriptRef.path}`}
      onOpen={onOpen}
      action="View code"
      icon={<CodeOutlinedIcon fontSize="small" />}
      primary={(
        <Typography variant="body2" sx={{ fontFamily: MONO, wordBreak: 'break-word' }}>{scriptRef.path}</Typography>
      )}
      secondary={<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{secondary}</Typography>}
    />
  )
}

/** A command, or a script with no viewer behind it: shown, not opened. */
const RecordRefCard: React.FC<{ primary: string; secondary: string }> = ({ primary, secondary }) => (
  <Box sx={{ px: 2, py: 1.25, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1.5 }}>
    <Typography variant="body2" sx={{ fontFamily: MONO, wordBreak: 'break-word' }}>
      {primary}
    </Typography>
    <Typography variant="caption" color="text.secondary">{secondary}</Typography>
  </Box>
)

/**
 * Where a warehouse pull's rows came from: `pt warehouse query --save` and the
 * `warehouse_query` tool write `{columns, rows, row_count, truncated, source}`,
 * and `source` names the connection and the SQL (or the saved query) and when
 * it ran. It is the far end of the trail for a table built from a database:
 * there is no cell behind a row a query returned.
 */
interface WarehouseSource {
  connection: string
  sql?: string
  saved_query?: string
  ran_at?: string
  query_log_id?: string
}
interface WarehousePull {
  row_count?: number
  truncated?: boolean
  source: WarehouseSource
}

/** The loaded file as a warehouse pull, or null when it is anything else (a
 *  crosstab, a composite). Loaded through the same loader as a dataset: both
 *  are `data/<name>.json`, and the loaders return the file as stored. */
function warehousePullOf(file: unknown): WarehousePull | null {
  const source = (file as { source?: unknown } | null)?.source as Partial<WarehouseSource> | undefined
  if (!source || typeof source !== 'object' || typeof source.connection !== 'string') return null
  if (typeof source.sql !== 'string' && typeof source.saved_query !== 'string') return null
  return file as WarehousePull
}

const formatRanAt = (iso?: string): string | null => {
  const ran = iso ? new Date(iso) : null
  return ran && !Number.isNaN(ran.getTime()) ? ran.toLocaleString() : null
}

/** One input of a table's run: a warehouse pull shows its connection and SQL
 *  (the SQL as TEXT in a <pre>, never markup: it is whatever the agent wrote);
 *  a table opens as one; a file the app does not ship is named and nothing
 *  more, rather than an error, because the receipt is still true. */
const RunInput: React.FC<{
  input: NonNullable<RunReceipt['inputs']>[number]
  loadDataset: DatasetLoader
  onOpen?: (view: Layer) => void
}> = ({ input, loadDataset, onOpen }) => {
  const { data, error } = useLoaded(input.dataset, loadDataset)
  if (error) return <RecordRefCard primary={input.dataset} secondary="input not shipped with this page" />
  if (!data) return null
  const pull = warehousePullOf(data)
  if (!pull) {
    const datasetRef: DatasetRef = { type: 'dataset', dataset: input.dataset, role: 'input' }
    return onOpen
      ? <DatasetRefCard datasetRef={datasetRef} onOpen={() => onOpen({ kind: 'dataset', datasetRef })} />
      : <RecordRefCard primary={input.dataset} secondary="input dataset" />
  }
  const { source } = pull
  const ran = formatRanAt(source.ran_at)
  const detail = [
    ran && `Run ${ran}`,
    typeof pull.row_count === 'number' && `${pull.row_count.toLocaleString()} ${pull.row_count === 1 ? 'row' : 'rows'}`,
  ].filter(Boolean).join(' · ')
  return (
    <Box
      role="group"
      aria-label={`Warehouse query ${input.dataset}`}
      data-testid="provenance-warehouse-query"
      sx={{ px: 2, py: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Data connection · {input.dataset}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{source.connection}</Typography>
      {detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{detail}</Typography>}
      {pull.truncated && (
        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
          The result was cut off at the row limit, so it is not every row.
        </Typography>
      )}
      {source.sql ? (
        <Box
          component="pre"
          tabIndex={0}
          role="region"
          aria-label={`SQL of ${input.dataset}`}
          data-testid="provenance-warehouse-sql"
          style={{ maxHeight: '14rem' }}
          sx={{
            m: 0, mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', fontFamily: MONO,
            fontSize: '0.8125rem', lineHeight: 1.6, overflow: 'auto', whiteSpace: 'pre',
          }}
        >
          {source.sql}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ mt: 0.5 }}>Saved query: {source.saved_query}</Typography>
      )}
    </Box>
  )
}

/**
 * How a source table was built, when a script built it. `save_dataset` writes
 * a table with an EMPTY cells map and one run receipt, because it is handed
 * finished numbers: no cell of it points anywhere. Without this the trail
 * stopped at the grid, although the file already said which code ran, over
 * which inputs, and (for a warehouse pull) which SQL on which connection.
 */
const TableBuild: React.FC<{
  run: RunReceipt
  loadDataset: DatasetLoader
  loadScript?: TextLoader
  onOpen?: (view: Layer) => void
}> = ({ run, loadDataset, loadScript, onOpen }) => {
  const scriptRef: ScriptRef | null = run.script ? { type: 'script', path: run.script } : null
  const openable = scriptRef && loadScript && onOpen && appRelativeScriptPath(scriptRef.path) !== null
  return (
    <Box role="group" aria-label="How this table was built" sx={{ mb: 1.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 2 }}>
        How this table was built
      </Typography>
      <ReplayReceipt run={run} />
      <Stack spacing={1}>
        {scriptRef && (
          <ScriptRefCard
            scriptRef={scriptRef}
            onOpen={openable ? () => onOpen({ kind: 'script', scriptRef }) : undefined}
          />
        )}
        {(run.inputs ?? []).map((input) => (
          <RunInput key={input.dataset} input={input} loadDataset={loadDataset} onOpen={onOpen} />
        ))}
      </Stack>
    </Box>
  )
}

/** Shape A: the source table, focused on the cited cell. */
const SourceTableView: React.FC<{
  cellRef: CellRef
  loadDataset: DatasetLoader
  loadScript?: TextLoader
  /** Opens a layer above (the script, an input table). None: shown, not opened. */
  onOpen?: (view: Layer) => void
}> = ({ cellRef, loadDataset, loadScript, onOpen }) => {
  const { data, error } = useLoaded(cellRef.dataset, loadDataset)

  if (error) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        Could not load the source table <strong>{cellRef.dataset}</strong>: {error}
      </Alert>
    )
  }
  if (!data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
  }
  // A source that is itself a composite carries its own provenance sidecar
  // for this cell. When it does, the source's `freq` is an unscaled value
  // reused across units — reading it as the crosstab metric (Count/Column
  // %/Row %) mislabels it, sometimes by 100x. Read it by that provenance's
  // own `unit` instead, and offer the chain beneath.
  const innerProvenance = provenanceFor(data, cellRef.row, cellRef.col)
  // A table a script built: no cell of it has provenance, but the file holds
  // the receipt of the run that wrote it.
  const tableRun = innerProvenance ? undefined : sidecarOf(data)?.run

  // The cited cell's detail lives HERE, above the grid, rather than in the
  // table's own info popover. Opened automatically, that popover's three cards
  // (values, column path, row path) landed on top of the grid and hid the
  // very table being cited. The reader can still click any cell for them.
  const stored = (data.cells?.rows?.[cellRef.row]?.cell?.[cellRef.col] ?? {}) as Partial<Record<CellRef['metric'], unknown>>
  const values = innerProvenance
    ? [{ metric: cellRef.metric, name: UNIT_NAME[innerProvenance.unit], text: inUnit(cellRef.sourceValue, innerProvenance.unit) }]
    : CITED_METRICS.flatMap(([metric, name]) => {
      // The cited metric shows the value the provenance recorded; the others
      // show what the cell holds, when it holds them.
      const v = metric === cellRef.metric ? cellRef.sourceValue : stored[metric]
      return typeof v === 'number' ? [{ metric, name, text: readableValue({ ...cellRef, metric, sourceValue: v }) }] : []
    })
  return (
    <Box>
      <Box
        role="group"
        aria-label="Cited cell"
        sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', columnGap: 3, rowGap: 1, mb: 1.5 }}
      >
        <Box sx={{ flex: '1 1 12rem', minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Source table · {cellRef.dataset}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{cellRef.rowLabel} × {cellRef.colLabel}</Typography>
        </Box>
        <Stack direction="row" spacing={3}>
          {values.map(({ metric, name, text }) => {
            const cited = metric === cellRef.metric
            return (
              <Box key={metric}>
                <Typography variant="caption" sx={{ display: 'block', color: cited ? 'info.main' : 'text.secondary', fontWeight: cited ? 600 : 400 }}>
                  {name}{cited ? ' · cited' : ''}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: cited ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>{text}</Typography>
              </Box>
            )
          })}
        </Stack>
      </Box>
      {tableRun && <TableBuild run={tableRun} loadDataset={loadDataset} loadScript={loadScript} onOpen={onOpen} />}
      <Box
        data-testid="provenance-source-table"
        // Bounded, so PlatinumTable's own root (overflow: auto, its direct
        // child) is the scroller in BOTH axes: its sideways scrollbar sits at
        // the bottom of this box, on screen, rather than at the foot of a
        // full-height table far below the fold; and its sticky row labels and
        // headers have a scroller to stick within. A column flex box because a
        // max-height alone gives the child's height: 100% nothing to resolve
        // against; as a flex item it shrinks to fit instead. Inline so the
        // bound is testable in jsdom.
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '62vh' }}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      >
        <PlatinumTable
          data={data}
          // DATA indices. Highlighted and scrolled to, deliberately NOT opened.
          highlightCell={{ rowIndex: cellRef.row, colIndex: cellRef.col }}
        />
      </Box>
      {innerProvenance && (
        <ChainStep
          provenance={innerProvenance}
          depth={1}
          visited={new Set([`${cellRef.dataset}:${cellRef.row},${cellRef.col}`])}
          loadDataset={loadDataset}
        />
      )}
    </Box>
  )
}

/** Loads one named thing, cancelling on change. Shared by every view here so
 *  that a load that fails states its reason rather than leaving a blank modal
 *  — a blank reads as a broken feature, a reason reads as a missing file. */
function useLoaded<T>(key: string, load: (key: string) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    load(key)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e: Error) => { if (!cancelled) setError(e?.message || String(e)) })
    return () => { cancelled = true }
  }, [key, load])
  return { data, error }
}

/** A whole dataset a script cited. An 'intermediate' renders as a composite —
 *  its cells carry their own provenance, so the recursive drill-down that a
 *  cell ref starts simply continues from here. An 'input' is an ordinary
 *  crosstab and renders as one. */
const DatasetView: React.FC<{
  datasetRef: DatasetRef
  loadDataset: DatasetLoader
  onOpenProvenance: (provenance: CellProvenance) => void
}> = ({ datasetRef, loadDataset, onOpenProvenance }) => {
  const { data, error } = useLoaded(datasetRef.dataset, loadDataset)

  if (error) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        Could not load <strong>{datasetRef.dataset}</strong>: {error}
      </Alert>
    )
  }
  if (!data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
  }

  const sidecar = sidecarOf(data)
  const shape = datasetShape(datasetRef)
  // A panel `save_dataset` wrote carries `{cells: {}, run}` deliberately: it is
  // handed finished numbers and cannot know each one's origin, so per-cell
  // provenance would be the run receipt copied once per cell — the very thing
  // the top-level receipt exists to avoid. So the trail ENDS here, correctly,
  // and the reader who clicked in expecting to keep going is told why. A
  // statement, not an error: nothing has gone wrong.
  const terminal = datasetRef.role === 'intermediate' && Object.keys(sidecar?.cells ?? {}).length === 0
  return (
    <Box>
      <Box role="group" aria-label="Cited dataset" sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {GROUP_TITLE[datasetRef.role]} · {datasetRef.dataset}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {datasetRef.note || shape || datasetRef.dataset}
        </Typography>
        {datasetRef.note && shape && (
          <Typography variant="caption" color="text.secondary">{shape}</Typography>
        )}
      </Box>
      {/* The panel's OWN receipt, not the citing composite's — this is the run
          that built the table now on screen. */}
      {sidecar?.run && <ReplayReceipt run={sidecar.run} />}
      {terminal && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This is the table the model ran on. Its figures were computed rather than lifted from a
          source cell, so the trail ends here{sidecar?.run ? ' — the receipt above covers how they were produced' : ''}.
        </Typography>
      )}
      <Box
        data-testid="provenance-dataset-table"
        style={sidecar
          ? { maxHeight: '62vh', overflow: 'auto' }
          : { display: 'flex', flexDirection: 'column', maxHeight: '62vh' }}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, ...(sidecar ? {} : { overflow: 'hidden' }) }}
      >
        {sidecar ? (
          <CompositeTable
            data={data as never}
            provenance={sidecar}
            onCellClick={(_row, _col, prov) => onOpenProvenance(prov)}
          />
        ) : (
          <PlatinumTable data={data} />
        )}
      </Box>
    </Box>
  )
}

/** The code itself. Rendered as TEXT into a <pre> — never as markup: the file
 *  is arbitrary bytes out of a sandbox, and interpreting it as HTML would run
 *  whatever it contains inside the app's own origin. */
const ScriptView: React.FC<{ scriptRef: ScriptRef; loadScript: TextLoader }> = ({ scriptRef, loadScript }) => {
  // Only ever opened for a path the app ships (ScriptRefCard gates the button
  // on the same call), so the ?? is unreachable rather than a fallback.
  const { data, error } = useLoaded(appRelativeScriptPath(scriptRef.path) ?? scriptRef.path, loadScript)

  return (
    <Box>
      <Box role="group" aria-label="Script" sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Script</Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: MONO, wordBreak: 'break-word' }}>
          {scriptRef.path}
        </Typography>
        {scriptRef.command && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO, wordBreak: 'break-word' }}>
            ran as: {scriptRef.command}
          </Typography>
        )}
      </Box>
      {error ? (
        <Alert severity="warning" sx={{ my: 1 }}>
          Could not read <strong>{scriptRef.path}</strong>: {error}
        </Alert>
      ) : data === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : (
        <Box
          component="pre"
          data-testid="provenance-script-source"
          // Focusable and named: the box scrolls, and a scrollable region a
          // keyboard cannot reach is unreadable without a mouse.
          tabIndex={0}
          role="region"
          aria-label={`Source of ${scriptRef.path}`}
          style={{ maxHeight: '62vh' }}
          sx={{
            m: 0,
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: MONO,
            fontSize: '0.8125rem',
            lineHeight: 1.6,
            overflow: 'auto',
            whiteSpace: 'pre',
            tabSize: 4,
          }}
        >
          {data}
        </Box>
      )}
    </Box>
  )
}

/** How an input joins the ones above it. A word the reader would say aloud
 *  becomes its symbol; anything longer ("compared with") stays a word. */
const OPERATOR_SYMBOL: Record<string, string> = { minus: '−', plus: '+', 'divided by': '÷' }

/** The worked calculation: inputs stacked like a sum on paper, then the result. */
const Calculation: React.FC<{ explanation: OpExplanation }> = ({ explanation }) => (
  <Box
    role="group"
    aria-label="Calculation"
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(1.5em, auto) 1fr auto',
      columnGap: 1.5,
      rowGap: 0.5,
      alignItems: 'baseline',
      px: 2,
      py: 1.5,
      mb: 2,
      borderRadius: 1,
      bgcolor: 'action.hover',
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    {explanation.lines.map((line, i) => (
      <React.Fragment key={i}>
        <Typography variant="body2" color="text.secondary" aria-label={line.operator}>
          {line.operator ? (OPERATOR_SYMBOL[line.operator] ?? line.operator) : ''}
        </Typography>
        <Typography variant="body2">{line.label}</Typography>
        <Typography variant="body2" sx={{ textAlign: 'right' }}>{line.value}</Typography>
      </React.Fragment>
    ))}
    <Box sx={{ gridColumn: '1 / -1', borderTop: 1, borderColor: 'divider', my: 0.5 }} />
    <Typography variant="body2" color="text.secondary">=</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{explanation.result.label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{explanation.result.value}</Typography>
  </Box>
)

/**
 * The replay receipt: this code, over these inputs, produced this number again.
 *
 * It is the strongest claim the dialog can make, and the only one the agent
 * cannot make about itself — `reproduced` is written by validate_provenance —
 * so it is stated once, calmly, in the semantic colour and nothing more. No
 * badge, no fill, no icon-only shorthand: a tick a reader has to decode is
 * decoration, and this is evidence.
 */
const RECEIPT_STATE = {
  yes: {
    title: 'Reproduced',
    body: 'This code was re-run over these inputs and produced this number again.',
    colour: 'success' as const,
    Icon: CheckCircleOutlineIcon,
  },
  no: {
    title: 'Not reproduced',
    body: 'This code was re-run over these inputs and did NOT produce this number again.',
    colour: 'warning' as const,
    Icon: ErrorOutlineIcon,
  },
  unchecked: {
    title: 'Produced by a recorded run',
    body: 'The command and its inputs were recorded. It has not been re-run to check.',
    colour: 'info' as const,
    Icon: InfoOutlinedIcon,
  },
}

const ReplayReceipt: React.FC<{ run: RunReceipt }> = ({ run }) => {
  const state = RECEIPT_STATE[run.reproduced === true ? 'yes' : run.reproduced === false ? 'no' : 'unchecked']
  const { Icon } = state
  const ran = run.ranAt ? new Date(run.ranAt) : null
  return (
    <Box
      role="group"
      aria-label="Replay receipt"
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        mb: 2,
        border: 1,
        borderColor: alpha(theme.palette[state.colour].main, 0.4),
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette[state.colour].main, 0.06),
      })}
    >
      <Icon fontSize="small" sx={{ color: `${state.colour}.main`, mt: '2px', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: `${state.colour}.main`, fontWeight: 600 }}>
          {state.title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>{state.body}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: MONO, wordBreak: 'break-word' }}>
          {run.command}
        </Typography>
        {ran && !Number.isNaN(ran.getTime()) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Run {ran.toLocaleString()}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/** A composite-of-a-composite chain, one hop per nested source. Recurses
 *  through the cited cell's OWN provenance (a "which came from…" trail),
 *  capped in depth and guarded against cycles, since nothing stops an agent
 *  from citing a composite that (transitively) cites itself. */
const MAX_CHAIN_DEPTH = 5

const ChainStep: React.FC<{
  provenance: CellProvenance
  depth: number
  visited: Set<string>
  loadDataset: DatasetLoader
}> = ({ provenance, depth, visited, loadDataset }) => {
  // A derived cell built by a spec carries a structured `op`, explained the
  // same way the top-level audit trail (Shape B) explains one — reusing
  // explainOp is what keeps the two readings consistent.
  const explanation = explainOp(provenance)
  const cellRefs = provenance.refs.filter((r): r is CellRef => r.type === 'cell')
  return (
    <Box sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        Which came from…
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: explanation ? 1 : 1.5 }}>
        {explanation ? explanation.sentence : provenance.description || 'Taken directly from a source cell.'}
      </Typography>
      {explanation && <Calculation explanation={explanation} />}
      {cellRefs.map((ref, i) => (
        <ChainedCellRef key={refKey(ref, i)} cellRef={ref} depth={depth} visited={visited} loadDataset={loadDataset} />
      ))}
    </Box>
  )
}

/** One hop: loads the referenced dataset, reads the cited cell by that
 *  dataset's OWN provenance unit when it has one (never the citing metric),
 *  and — if that dataset is itself a composite — recurses via ChainStep. */
const ChainedCellRef: React.FC<{
  cellRef: CellRef
  depth: number
  visited: Set<string>
  loadDataset: DatasetLoader
}> = ({ cellRef, depth, visited, loadDataset }) => {
  const { data, error } = useLoaded(cellRef.dataset, loadDataset)

  if (error) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        Could not load <strong>{cellRef.dataset}</strong>: {error}
      </Alert>
    )
  }
  if (!data) {
    return <Box sx={{ display: 'flex', py: 1 }}><CircularProgress size={18} /></Box>
  }

  const inner = provenanceFor(data, cellRef.row, cellRef.col)
  const unit = inner?.unit
  const label = unit ? UNIT_NAME[unit] : METRIC_NAME[cellRef.metric]
  const text = unit ? inUnit(cellRef.sourceValue, unit) : readableValue(cellRef)
  const visitKey = `${cellRef.dataset}:${cellRef.row},${cellRef.col}`
  const atCap = depth >= MAX_CHAIN_DEPTH
  const isCycle = visited.has(visitKey)

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {cellRef.dataset} · {cellRef.rowLabel} × {cellRef.colLabel}
      </Typography>
      <Typography variant="body2">{label}: {text}</Typography>
      {inner && (isCycle || atCap) && (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          {isCycle
            ? 'This cell already appeared earlier in this chain — stopping here to avoid a loop.'
            : 'Chain trace stopped after 5 hops.'}
        </Alert>
      )}
      {inner && !isCycle && !atCap && (
        <ChainStep provenance={inner} depth={depth + 1} visited={new Set(visited).add(visitKey)} loadDataset={loadDataset} />
      )}
    </Box>
  )
}

/**
 * The exact record, behind a disclosure: stored values unrounded, data indices,
 * the op signature and the generated audit text. Collapsed by default because
 * it is for checking, not for reading — but always one click away, and always
 * in the DOM, so nothing checkable is hidden from a search or a screen reader.
 */
const TechnicalDetail: React.FC<{ lines: string[] }> = ({ lines }) => {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <Box sx={{ mb: 2 }}>
      <Button
        size="small"
        color="info"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={id}
        endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
        sx={{ px: 0.5, textTransform: 'none' }}
      >
        Technical detail
      </Button>
      <Collapse in={open} id={id}>
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.5,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: MONO,
            fontSize: '0.75rem',
            color: 'text.secondary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {lines.join('\n')}
        </Box>
      </Collapse>
    </Box>
  )
}

/** A group of references under its own heading. The heading appears only when
 *  there is more than one group to tell apart — one kind of reference needs no
 *  label, and an unnecessary heading is density without clarity. */
const RefGroup: React.FC<{ title: string; showTitle: boolean; children: React.ReactNode }> = ({ title, showTitle, children }) => (
  <Box>
    {showTitle && (
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
    )}
    <Stack spacing={1}>{children}</Stack>
  </Box>
)

/** Shape B: the audit trail plus its references. */
const AuditTrailView: React.FC<{
  provenance: CellProvenance
  onOpen: (layer: Layer) => void
  loadScript?: TextLoader
  run?: RunReceipt
}> = ({ provenance, onOpen, loadScript, run }) => {
  // A spec-built cell carries a structured `op`, so it is explained from that
  // rather than from its generated description: exact but unreadable ("diff:
  // mc_con_comp[1,24] (Sky x Aug-26) colpc=0.1987... minus ..."). A hand-built
  // derived cell has no op, and its description, written by the agent, IS the
  // explanation.
  const explanation = explainOp(provenance)
  const technical = [
    ...(explanation
      ? [...explanation.technical, `kind: ${provenance.kind}`, `recorded as: ${provenance.description}`]
      : [
          `kind: ${provenance.kind} · unit: ${provenance.unit}`,
          ...provenance.refs.flatMap((r, i) => refTechnical(r, i)),
        ]),
    ...(run ? runTechnical(run) : []),
  ]

  // Grouped, because "the inputs are named", "the middle is shown" and "the
  // code ships" are three separate promises and the reader is checking each.
  // Each entry keeps its ORIGINAL index, so a card's key and its line in the
  // technical detail (refs[i]) name the same reference after grouping.
  const indexed = provenance.refs.map((ref, index) => ({ ref, index }))
  const groups = [
    { key: 'cell' as const, title: GROUP_TITLE.cell, items: indexed.filter(({ ref }) => ref.type === 'cell') },
    { key: 'input' as const, title: GROUP_TITLE.input, items: indexed.filter(({ ref }) => ref.type === 'dataset' && ref.role === 'input') },
    { key: 'intermediate' as const, title: GROUP_TITLE.intermediate, items: indexed.filter(({ ref }) => ref.type === 'dataset' && ref.role === 'intermediate') },
    { key: 'code' as const, title: GROUP_TITLE.code, items: indexed.filter(({ ref }) => ref.type === 'script' || ref.type === 'command') },
  ].filter((g) => g.items.length > 0)
  const showTitles = groups.length > 1

  return (
    <Box>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
        {explanation ? explanation.sentence : provenance.description}
      </Typography>
      {explanation && <Calculation explanation={explanation} />}
      {run && <ReplayReceipt run={run} />}
      <TechnicalDetail lines={technical} />
      <Divider />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        {provenance.refs.length === 0
          ? 'No references recorded'
          : `${provenance.refs.length} reference${provenance.refs.length === 1 ? '' : 's'}`}
      </Typography>
      <Stack spacing={2}>
        {groups.map((group) => (
          <RefGroup key={group.key} title={group.title} showTitle={showTitles}>
            {group.items.map(({ ref, index }) => {
              const key = refKey(ref, index)
              switch (ref.type) {
                case 'cell':
                  return <CellRefCard key={key} cellRef={ref} onOpen={() => onOpen({ kind: 'cell', cellRef: ref })} />
                case 'dataset':
                  return <DatasetRefCard key={key} datasetRef={ref} onOpen={() => onOpen({ kind: 'dataset', datasetRef: ref })} />
                case 'script':
                  return (
                    <ScriptRefCard
                      key={key}
                      scriptRef={ref}
                      onOpen={
                        loadScript && appRelativeScriptPath(ref.path)
                          ? () => onOpen({ kind: 'script', scriptRef: ref })
                          : undefined
                      }
                    />
                  )
                case 'command':
                  return <RecordRefCard key={key} primary={ref.command} secondary="command" />
              }
            })}
          </RefGroup>
        ))}
      </Stack>
    </Box>
  )
}

/**
 * One dialog above the current one. A stack rather than a single slot, because
 * an intermediate dataset opens as a composite whose own cells open their own
 * audit trail, which cites more datasets — the trail is as deep as the work
 * was. No depth cap here (unlike ChainStep, which recurses on its own): every
 * layer costs the reader a deliberate click, so a cycle cannot run away.
 */
type Layer =
  | { kind: 'cell'; cellRef: CellRef }
  | { kind: 'dataset'; datasetRef: DatasetRef }
  | { kind: 'script'; scriptRef: ScriptRef }
  | { kind: 'audit'; provenance: CellProvenance }

const LAYER_CHROME: Record<Layer['kind'], { title: string; closeLabel: string }> = {
  cell: { title: 'Where this number came from', closeLabel: 'Close the source table' },
  dataset: { title: 'The data behind this number', closeLabel: 'Close the data table' },
  script: { title: 'The code that produced this number', closeLabel: 'Close the code' },
  audit: { title: 'How this number was produced', closeLabel: 'Close the working' },
}

export const ProvenanceModal: React.FC<ProvenanceModalProps> = ({
  open, onClose, provenance, loadDataset, loadScript, run, title, actions,
}) => {
  // Layers opened from the audit trail, each its own dialog stacked on top —
  // two real dialogs rather than one swapping its content, so the modal manager
  // gives each layer its own Escape (only the top one closes), its own focus
  // trap, and focus back to the reference card on close.
  //
  // `open` is kept apart from the layer so the table stays rendered while the
  // top layer fades out; the layer is dropped only once the exit has finished.
  const [layers, setLayers] = useState<Array<{ id: number; view: Layer; open: boolean }>>([])
  const nextId = useRef(0)

  useEffect(() => { if (!open) setLayers([]) }, [open])

  const pushLayer = useCallback((view: Layer) => {
    nextId.current += 1
    setLayers((ls) => [...ls, { id: nextId.current, view, open: true }])
  }, [])
  const closeLayer = useCallback((id: number) => {
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, open: false } : l)))
  }, [])
  const dropLayer = useCallback((id: number) => {
    setLayers((ls) => ls.filter((l) => l.id !== id || l.open))
  }, [])

  if (!provenance) return null

  const directRef = provenance.kind === 'direct'
    ? provenance.refs.find((r): r is CellRef => r.type === 'cell') ?? null
    : null

  const renderLayer = (view: Layer) => {
    switch (view.kind) {
      case 'cell':
        return <SourceTableView cellRef={view.cellRef} loadDataset={loadDataset} loadScript={loadScript} onOpen={pushLayer} />
      case 'dataset':
        return (
          <DatasetView
            datasetRef={view.datasetRef}
            loadDataset={loadDataset}
            onOpenProvenance={(p) => pushLayer({ kind: 'audit', provenance: p })}
          />
        )
      case 'script':
        // Only ever pushed when a loader exists — the card is not a button
        // without one — but typed defensively rather than asserted.
        return loadScript ? <ScriptView scriptRef={view.scriptRef} loadScript={loadScript} /> : null
      case 'audit':
        return <AuditTrailView provenance={view.provenance} onOpen={pushLayer} loadScript={loadScript} />
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <ModalHeader
          title={title || (provenance.kind === 'direct' ? 'Where this number came from' : 'How this number was produced')}
          onClose={onClose}
          closeLabel="Close"
        />
        <DialogContent dividers>
          {directRef ? (
            <>
              {/* The receipt covers the composite, not the cell, so it is shown
                  whichever shape the cell takes. */}
              {run && <ReplayReceipt run={run} />}
              <SourceTableView cellRef={directRef} loadDataset={loadDataset} loadScript={loadScript} onOpen={pushLayer} />
            </>
          ) : provenance.kind === 'direct' ? (
            <>
              {run && <ReplayReceipt run={run} />}
              <Alert severity="warning">
                This cell is marked as taken directly from a source table, but carries no cell
                reference, so there is nothing to show.
              </Alert>
            </>
          ) : (
            <AuditTrailView provenance={provenance} onOpen={pushLayer} loadScript={loadScript} run={run} />
          )}
        </DialogContent>
        {actions && actions.length > 0 && (
          <DialogActions>
            {actions.map((a) => (
              <Button
                key={a.label}
                color="info"
                onClick={() => {
                  a.onClick()
                  onClose()
                }}
              >
                {a.label}
              </Button>
            ))}
          </DialogActions>
        )}
      </Dialog>
      {layers.map((layer) => (
        <Dialog
          key={layer.id}
          open={open && layer.open}
          onClose={() => closeLayer(layer.id)}
          maxWidth="lg"
          fullWidth
          slotProps={{ transition: { onExited: () => dropLayer(layer.id) } }}
        >
          <ModalHeader
            title={LAYER_CHROME[layer.view.kind].title}
            onClose={() => closeLayer(layer.id)}
            closeLabel={LAYER_CHROME[layer.view.kind].closeLabel}
          />
          <DialogContent dividers>{renderLayer(layer.view)}</DialogContent>
        </Dialog>
      ))}
    </>
  )
}

const ModalHeader: React.FC<{ title: string; onClose: () => void; closeLabel: string }> = ({ title, onClose, closeLabel }) => (
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
    <Typography variant="h6" component="span" sx={{ flex: 1 }}>{title}</Typography>
    <IconButton size="small" onClick={onClose} aria-label={closeLabel}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogTitle>
)

export default ProvenanceModal
